import { forwardRef, Ref } from "react";
import styled from "styled-components";
import { isNumber } from "@shared/common/utils/typing";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly label?: string;
  readonly width?: number | string;
  readonly noBorder?: boolean;
  readonly "data-testid"?: string;
};

type OtherProps = {
  $error: boolean;
  $noBorder: boolean;
  $width?: string | number;
  $height?: string | number;
};

const getWidthStyle = ($width?: string | number) => {
  if (!$width) return "";
  return isNumber($width) ? `width: ${$width}px` : `width: ${$width}`;
};

const getHeightStyle = ($height?: string | number) => {
  if (!$height) return "";
  return isNumber($height) ? `height: ${$height}px` : `height: ${$height}`;
};

const LocalInput = styled.input<
  OtherProps & Omit<InputProps, "error" | "noBorder" | "width" | "height">
>`
  border: 0;
  ${({ $width }) => getWidthStyle($width)};
  ${({ $height }) => getHeightStyle($height)};
  font-size: 16px;
  font-family: ${({ theme }) => theme.typography.family};
  background-color: ${({ theme }) => theme.colors.backgroundInput};
  outline: none;
  display: block;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $noBorder, $error }) =>
      $noBorder ? "none" : $error ? theme.colors.borderError : theme.colors.borderInput};
  color: ${({ theme }) => theme.colors.content};
  transition-duration: 0.3s;
  transition-property:
    color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow,
    transform, filter, backdrop-filter, -webkit-backdrop-filter;
  transition-timing-function: cubic-bezier(0.2, 0.9, 0.4, 1);
  box-sizing: border-box;

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundInput};
    border: 1px solid
      ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderInputHover)};
  }

  &:focus {
    border: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
    outline: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
  }

  &[data-state="open"] {
    border: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
    outline: 1px solid ${({ theme, $noBorder }) => ($noBorder ? "none" : theme.colors.borderFocus)};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.contentPlaceholder};
  }
`;

const ErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.sizeSM.toString()};
  font-family: ${({ theme }) => theme.typography.family};
  color: ${({ theme }) => theme.colors.contentError};
  display: inline-block;
  word-wrap: break-word;
  line-height: 1.5;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(InnerInput);
function InnerInput(
  {
    type,
    value,
    defaultValue,
    name,
    placeholder,
    error = false,
    disabled,
    onChange,
    errorMessage,
    autoComplete,
    onKeyDown,
    height = 41,
    width = "100%",
    noBorder = false,
    "data-testid": inputTestId,
    ...props
  }: InputProps,
  ref: Ref<HTMLInputElement>,
) {
  return (
    <>
      <LocalInput
        $width={width}
        $height={height}
        ref={ref}
        type={type}
        value={value}
        defaultValue={defaultValue}
        name={name}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        data-testid={inputTestId}
        $error={error}
        $noBorder={noBorder}
        label="label"
        autoComplete={autoComplete}
        onKeyDown={onKeyDown}
        {...props}
      />
      {error && (
        <div
          style={{
            width: width ? `${width}px` : "100%",
            height: 24,
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: 4,
            display: "inline-flex",
          }}
        >
          <div
            style={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              display: "flex",
            }}
          >
            <div style={{ width: 24, height: 24, position: "relative" }}>
              <tractie-icon name="alert-filled" size="xs" color="--tractie-color-content-error" />
            </div>
          </div>
          <div
            style={{
              flex: "1 1 0",
              height: "24px",
              paddingTop: "2px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: "10px",
              display: "flex",
            }}
          >
            <ErrorMessage>{errorMessage}</ErrorMessage>
          </div>
        </div>
      )}
    </>
  );
}
